import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Eye, EyeOff, Link2, ShieldCheck } from "lucide-react";
import styles from "./Login.module.css";
import { MigrationSelection, readMigrationSelection, responseStringField } from "./loginResponse";
import { clearCurrentUserCache } from "../../api/currentUser";
import { Alert, Button, FormField, Heading, Input, LivingBackground, SpotlightCard, Text } from "../../components/ui";

interface LoginProps {
    testLogin?: boolean;
}

const Login = ({ testLogin = false }: LoginProps) => {
    const { t } = useTranslation();
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const mode = params.get("mode") === "forgot" ? "forgot" : testLogin ? "test" : params.get("mode") === "migrate" ? "migrate" : "providers";
    const [email, setEmail] = useState(params.get("email") ?? "");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [resetToken, setResetToken] = useState(params.get("reset_token") ?? "");
    const [proofEmail, setProofEmail] = useState("");
    const [pendingIdentity, setPendingIdentity] = useState<MigrationSelection | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [checkingProof, setCheckingProof] = useState(mode === "migrate");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(params.get("error") ?? "");
    const [success, setSuccess] = useState("");
    const returnTo = params.get("returnTo") === "/invitation" ? "/invitation" : "/app";

    useEffect(() => {
        if (!params.has("reset_token")) return;
        const clean = new URLSearchParams(params);
        clean.delete("reset_token");
        setParams(clean, { replace: true });
    }, [params, setParams]);

    useEffect(() => {
        if (mode !== "migrate") return;
        let active = true;
        setCheckingProof(true);
        void fetch("/auth/migration/status", { credentials: "same-origin", cache: "no-store" })
            .then(async response => {
                const data: unknown = await response.json();
                if (active) {
                    setProofEmail(response.ok ? (responseStringField(data, "email") ?? "") : "");
                    setPendingIdentity(response.ok ? readMigrationSelection(data) : null);
                }
            })
            .catch(() => {
                if (active) setError(t("accountMigration.connectionError"));
            })
            .finally(() => {
                if (active) setCheckingProof(false);
            });
        return () => {
            active = false;
        };
    }, [mode, t]);

    const changeMode = (next: "providers" | "migrate" | "forgot") => {
        setError("");
        setSuccess("");
        setPassword("");
        setConfirmPassword("");
        setResetToken("");
        const nextParams = new URLSearchParams();
        if (next !== "providers") nextParams.set("mode", next);
        if (returnTo === "/invitation") nextParams.set("returnTo", returnTo);
        setParams(nextParams);
    };

    const providerLogin = (provider: "google" | "microsoft") => {
        setError("");
        const query = new URLSearchParams();
        if (mode === "migrate" && proofEmail) query.set("flow", "migration");
        else if (returnTo === "/invitation") query.set("return_to", "invitation");
        window.location.assign(`/auth/login/${provider}${query.size ? `?${query.toString()}` : ""}`);
    };

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError("");
        setSuccess("");
        if (mode === "forgot" && resetToken && password !== confirmPassword) {
            setError(t("accountMigration.passwordsMismatch"));
            return;
        }
        setIsLoading(true);
        try {
            const endpoint =
                mode === "forgot" ? (resetToken ? "/auth/reset-password" : "/auth/forgot-password") : mode === "test" ? "/auth/login" : "/auth/migration/start";
            const body =
                mode === "forgot"
                    ? resetToken
                        ? { email, reset_token: resetToken, new_password: password }
                        : { email }
                    : { email, password, return_to: returnTo === "/invitation" ? "invitation" : "app" };
            const response = await fetch(endpoint, {
                method: "POST",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body)
            });
            const data: unknown = await response.json();
            if (!response.ok) {
                setError(responseStringField(data, "error") ?? t("accountMigration.requestFailed"));
                return;
            }
            setPassword("");
            if (mode === "forgot") {
                if (resetToken) {
                    changeMode(testLogin ? "providers" : "migrate");
                    setSuccess(t("accountMigration.resetSuccess"));
                } else setSuccess(t("accountMigration.resetSent"));
            } else if (mode === "test") {
                clearCurrentUserCache();
                navigate("/app", { replace: true });
            } else {
                clearCurrentUserCache();
                setProofEmail(responseStringField(data, "email") ?? email.trim());
                setPendingIdentity(null);
            }
        } catch {
            setError(t("accountMigration.connectionError"));
        } finally {
            setIsLoading(false);
        }
    };

    const cancelLinking = async () => {
        setIsLoading(true);
        try {
            const response = await fetch("/auth/migration/cancel", { method: "POST", credentials: "same-origin" });
            if (!response.ok) throw new Error("cancel failed");
            setProofEmail("");
            setPendingIdentity(null);
            changeMode("providers");
        } catch {
            setError(t("accountMigration.connectionError"));
        } finally {
            setIsLoading(false);
        }
    };

    const confirmIdentity = async () => {
        if (!pendingIdentity) return;
        setIsLoading(true);
        setError("");
        try {
            const response = await fetch("/auth/migration/confirm", {
                method: "POST",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ confirmation_token: pendingIdentity.confirmationToken })
            });
            const data: unknown = await response.json();
            if (!response.ok) {
                setError(responseStringField(data, "error") ?? t("accountMigration.requestFailed"));
                const status = await fetch("/auth/migration/status", { credentials: "same-origin", cache: "no-store" });
                const current: unknown = await status.json();
                setProofEmail(status.ok ? (responseStringField(current, "email") ?? "") : "");
                setPendingIdentity(status.ok ? readMigrationSelection(current) : null);
                return;
            }
            clearCurrentUserCache();
            navigate(responseStringField(data, "return_to") === "/invitation" ? "/invitation" : "/app", { replace: true });
        } catch {
            setError(t("accountMigration.connectionError"));
        } finally {
            setIsLoading(false);
        }
    };

    const chooseAnotherIdentity = async () => {
        setIsLoading(true);
        setError("");
        try {
            const response = await fetch("/auth/migration/reselect", { method: "POST", credentials: "same-origin" });
            if (!response.ok) throw new Error("selection expired");
            setPendingIdentity(null);
        } catch {
            setError(t("accountMigration.connectionError"));
        } finally {
            setIsLoading(false);
        }
    };

    const linking = mode === "migrate" && Boolean(proofEmail);
    const providerButtons = () => (
        <div className={styles.oauthButtons}>
            <Button
                variant="secondary"
                size="lg"
                fullWidth
                className={`${styles.oauthButton} ${styles.microsoftButton}`}
                disabled={isLoading}
                onClick={() => providerLogin("microsoft")}
                leftIcon={
                    <svg className={styles.icon} viewBox="0 0 23 23" aria-hidden="true">
                        <path fill="#f35325" d="M0 0h11v11H0z" />
                        <path fill="#81bc06" d="M12 0h11v11H12z" />
                        <path fill="#05a6f0" d="M0 12h11v11H0z" />
                        <path fill="#ffba08" d="M12 12h11v11H12z" />
                    </svg>
                }
            >
                {t(linking ? "accountMigration.linkMicrosoft" : "loginPage.continueWithMicrosoft")}
            </Button>
            <Button
                variant="secondary"
                size="lg"
                fullWidth
                className={`${styles.oauthButton} ${styles.googleButton}`}
                disabled={isLoading}
                onClick={() => providerLogin("google")}
                leftIcon={
                    <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden="true">
                        <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.7 7.15 9.14 5.38 12 5.38z"
                        />
                    </svg>
                }
            >
                {t(linking ? "accountMigration.linkGoogle" : "loginPage.continueWithGoogle")}
            </Button>
        </div>
    );

    return (
        <div className={`${styles.loginContainer} bg-glass-bg min-h-screen`}>
            <LivingBackground />
            <SpotlightCard className={styles.loginCard} enableTilt={false} style={{ position: "relative", zIndex: 10 }}>
                <div className={styles.logoContainer}>
                    <Heading level={1} className={styles.appName}>
                        {t("loginPage.appName")}
                    </Heading>
                    <Text as="p" variant="body" tone="secondary" className={styles.tagline}>
                        {t(
                            mode === "providers"
                                ? "loginPage.welcomeTitle"
                                : mode === "forgot"
                                  ? "accountMigration.resetTitle"
                                  : mode === "test"
                                    ? "accountMigration.testTitle"
                                    : linking
                                      ? pendingIdentity
                                          ? "accountMigration.confirmTitle"
                                          : "accountMigration.chooseProvider"
                                      : "accountMigration.verifyTitle"
                        )}
                    </Text>
                </div>
                <div aria-live="polite">
                    {error ? <Alert variant="error" description={error} className={styles.statusMessage} /> : null}
                    {success ? <Alert variant="success" description={success} className={styles.statusMessage} /> : null}
                </div>
                {mode === "providers" ? (
                    <>
                        {providerButtons()}
                        <button type="button" className={styles.migrationEntry} onClick={() => changeMode("migrate")}>
                            <span className={styles.migrationIcon}>
                                <Link2 size={22} aria-hidden="true" />
                            </span>
                            <span>
                                <strong>{t("accountMigration.entryTitle")}</strong>
                                <span className={styles.migrationDescription}>{t("accountMigration.entryDescription")}</span>
                            </span>
                            <ArrowRight size={20} aria-hidden="true" />
                        </button>
                    </>
                ) : linking ? (
                    <>
                        <div className={styles.linkingSummary}>
                            <ShieldCheck size={24} aria-hidden="true" />
                            <p>{t("accountMigration.originalAccount")}</p>
                            <strong>{proofEmail}</strong>
                            {pendingIdentity ? (
                                <>
                                    <p>{t("accountMigration.futureSignIn")}</p>
                                    <strong>{pendingIdentity.email}</strong>
                                    <span>{pendingIdentity.provider === "google" ? "Google" : "Microsoft"}</span>
                                    <p>{t("accountMigration.confirmConsequence")}</p>
                                    <p>{t("accountMigration.noDataMove")}</p>
                                </>
                            ) : (
                                <>
                                    <p>{t("accountMigration.sameEmailHint")}</p>
                                    <p>{t("accountMigration.preserveHint")}</p>
                                </>
                            )}
                        </div>
                        {pendingIdentity ? (
                            <div className={styles.oauthButtons}>
                                <Button
                                    variant="primary"
                                    size="lg"
                                    fullWidth
                                    className={styles.submitButton}
                                    isLoading={isLoading}
                                    onClick={() => void confirmIdentity()}
                                >
                                    {t("accountMigration.confirmLink")}
                                </Button>
                                <Button variant="secondary" fullWidth disabled={isLoading} onClick={() => void chooseAnotherIdentity()}>
                                    {t("accountMigration.chooseAnother")}
                                </Button>
                            </div>
                        ) : (
                            providerButtons()
                        )}
                        <Button variant="ghost" fullWidth disabled={isLoading} onClick={() => void cancelLinking()}>
                            {t("accountMigration.startOver")}
                        </Button>
                    </>
                ) : checkingProof && mode === "migrate" ? (
                    <p role="status">{t("loginPage.pleaseWait")}</p>
                ) : (
                    <>
                        {mode === "migrate" ? <p className={styles.formHint}>{t("accountMigration.verifyHint")}</p> : null}
                        <form onSubmit={event => void submit(event)} className={styles.emailForm}>
                            <FormField label={t("accountMigration.emailLabel")} htmlFor="email">
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    required
                                    autoComplete="username"
                                    disabled={isLoading || Boolean(resetToken)}
                                    onChange={event => setEmail(event.target.value)}
                                />
                            </FormField>
                            {mode !== "forgot" || resetToken ? (
                                <FormField label={t(resetToken ? "accountMigration.newPassword" : "accountMigration.passwordLabel")} htmlFor="password">
                                    <div className={styles.passwordWrapper}>
                                        <Input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            required
                                            minLength={resetToken ? 8 : undefined}
                                            autoComplete={resetToken ? "new-password" : "current-password"}
                                            disabled={isLoading}
                                            onChange={event => setPassword(event.target.value)}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            iconOnly
                                            className={styles.passwordToggle}
                                            aria-label={t(showPassword ? "accountMigration.hidePassword" : "accountMigration.showPassword")}
                                            aria-pressed={showPassword}
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </Button>
                                    </div>
                                </FormField>
                            ) : null}
                            {mode === "forgot" && resetToken ? (
                                <FormField label={t("accountMigration.confirmPassword")} htmlFor="confirm-password">
                                    <Input
                                        id="confirm-password"
                                        type={showPassword ? "text" : "password"}
                                        required
                                        autoComplete="new-password"
                                        value={confirmPassword}
                                        disabled={isLoading}
                                        onChange={event => setConfirmPassword(event.target.value)}
                                    />
                                </FormField>
                            ) : null}
                            <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isLoading} className={styles.submitButton}>
                                {t(
                                    mode === "forgot"
                                        ? resetToken
                                            ? "accountMigration.resetPassword"
                                            : "accountMigration.sendReset"
                                        : mode === "test"
                                          ? "loginPage.logIn"
                                          : "accountMigration.verifyContinue"
                                )}
                            </Button>
                            {mode !== "forgot" ? (
                                <button type="button" className={styles.forgotLink} disabled={isLoading} onClick={() => changeMode("forgot")}>
                                    {t("loginPage.forgotPassword")}
                                </button>
                            ) : null}
                        </form>
                        <Button
                            variant="ghost"
                            fullWidth
                            disabled={isLoading}
                            onClick={() => {
                                if (mode === "test") navigate("/login");
                                else changeMode(mode === "forgot" && !testLogin ? "migrate" : "providers");
                            }}
                        >
                            {t("accountMigration.back")}
                        </Button>
                    </>
                )}
                <div className={styles.footer}>
                    <Text as="p" variant="caption" tone="secondary" className={styles.footerText}>
                        <Link to="/terms" className={styles.legalLink}>
                            Terms of Service
                        </Link>
                        {" · "}
                        <Link to="/privacy" className={styles.legalLink}>
                            Privacy Policy
                        </Link>
                    </Text>
                </div>
            </SpotlightCard>
        </div>
    );
};

export default Login;
